/**
 * Shared facility / well name matching for typed search.
 *
 * Multi-word queries like "angel eyes" must match wells whose names contain
 * every token (not only the contiguous phrase), and rank phrase hits first.
 */

import { isNmOcdFacilityId } from '../../shared/facilityApiSearchIntent';

export function tokenizeFacilitySearchQuery(query: string): string[] {
  return String(query || '')
    .toLowerCase()
    .trim()
    .split(/[^a-z0-9]+/)
    .filter((token) => token.length >= 2);
}

/**
 * Digits / hyphens only (e.g. 32-025, 30025, 30-025-44913).
 * These must search API numbers — not well names like "TX Well 32025".
 */
export function looksLikeApiSearchQuery(query: string): boolean {
  const q = String(query || '').trim();
  if (!q || isNmOcdFacilityId(q)) return false;
  const digits = q.replace(/\D/g, '');
  return digits.length >= 3 && (digits.length / Math.max(q.length, 1) > 0.45 || /^[\d\-\s.]+$/.test(q));
}

/**
 * This product catalogs NM (30) and TX (42) only. Other official API state
 * prefixes (35 OK, 05 CO, …) will come later — do not rewrite them to 30-*.
 */
const OUT_OF_SCOPE_API_STATE = new Set([
  '01', '02', '03', '04', '05', '16', '17', '18', '20', '21', '22',
  '26', '28', '33', '34', '35', '37', '43', '45', '47', '48', '49', '50',
]);

/** NM or TX only. 32- is Lea shorthand, not a state. */
export function apiStateAbbrFromDigits(digits: string): string {
  const d = String(digits || '').replace(/\D/g, '');
  if (d.startsWith('30')) return 'NM';
  if (d.startsWith('42')) return 'TX';
  return '';
}

/** 35-029 / 05-045 etc. — not in the NM/TX catalog yet. */
export function queryIsOutOfScopeApiState(digits: string): boolean {
  const d = String(digits || '').replace(/\D/g, '');
  if (d.length < 5) return false;
  return OUT_OF_SCOPE_API_STATE.has(d.slice(0, 2));
}

/**
 * Field shorthand: 32-025 / 32025 means NM county 30-025 (Lea).
 * Only 32- aliases. 35-029 is Oklahoma and must not become 30-029.
 */
export function nmCountyPrefixFromShorthandDigits(digits: string): string | null {
  const d = String(digits || '').replace(/\D/g, '');
  // 32025 → 30025 (Lea). 3202511 → 3002511 (32-025-11 typed as an API prefix).
  if (d.length < 5) return null;
  if (!d.startsWith('32')) return null;
  return `30${d.slice(2)}`;
}

function apiDigitsOf(api: string): string {
  return String(api || '').replace(/\D/g, '');
}

export function compareApiNumberDigits(a: string, b: string): number {
  const da = apiDigitsOf(a);
  const db = apiDigitsOf(b);
  if (da === db) return 0;
  if (!da) return 1;
  if (!db) return -1;
  if (da.length !== db.length) return da.length - db.length;
  return da < db ? -1 : 1;
}

export function sortHitsByApiNumber<T>(
  hits: T[],
  getApi: (hit: T) => string,
  direction: 'asc' | 'desc' = 'desc',
): T[] {
  const dir = direction === 'asc' ? 1 : -1;
  return [...hits].sort((a, b) => dir * compareApiNumberDigits(getApi(a), getApi(b)));
}

function queryDigitsOf(query: string): string {
  return String(query || '').replace(/\D/g, '');
}

/** User already named a catalog state: 30-028 is NM, 42-445 is TX — not a last-5 well number. */
export function queryHasExplicitApiState(digits: string): boolean {
  const d = String(digits || '').replace(/\D/g, '');
  return d.startsWith('30') || d.startsWith('42');
}

function apiDigitsMatchQuery(api: string, query: string): boolean {
  const digits = queryDigitsOf(query);
  const apiDigits = apiDigitsOf(api);
  if (digits.length < 3 || !apiDigits) return false;
  if (queryIsOutOfScopeApiState(digits)) return false;
  if (apiDigits.startsWith(digits)) return true;
  const alias = nmCountyPrefixFromShorthandDigits(digits);
  if (alias && apiDigits.startsWith(alias)) return true;
  // Last-5 / substring only for shorthand (32-025 → Lea). Never for 30-* / 42-*.
  if (!queryHasExplicitApiState(digits) && apiDigits.includes(digits)) return true;
  return false;
}

/** True when name or API matches the query (phrase or all tokens). */
export function facilityNameMatchesQuery(name: string, api: string, query: string): boolean {
  const q = String(query || '').toLowerCase().trim();
  if (!q) return false;
  if (isNmOcdFacilityId(q)) {
    return String(api || '').toLowerCase().trim().startsWith(q);
  }
  if (looksLikeApiSearchQuery(q)) {
    return apiDigitsMatchQuery(api, q);
  }
  const n = String(name || '').toLowerCase();
  const a = String(api || '').toLowerCase();
  if (n.includes(q) || a.includes(q)) return true;
  const digits = q.replace(/\D/g, '');
  if (digits.length >= 3 && a.replace(/\D/g, '').includes(digits)) return true;
  const tokens = tokenizeFacilitySearchQuery(q);
  if (tokens.length === 0) return false;
  // All tokens must appear in the name (API digit tokens also allowed on API field).
  return tokens.every((token) => {
    if (n.includes(token)) return true;
    if (/\d/.test(token) && a.replace(/\D/g, '').includes(token.replace(/\D/g, ''))) return true;
    return false;
  });
}

function scoreApiNumberMatch(api: string, query: string): number {
  const digits = queryDigitsOf(query);
  const apiDigits = apiDigitsOf(api);
  if (digits.length < 3 || !apiDigits) return 0;
  if (queryIsOutOfScopeApiState(digits)) return 0;
  let score = 0;
  if (apiDigits === digits) score = 1000;
  else if (apiDigits.startsWith(digits)) score = 960;
  const alias = nmCountyPrefixFromShorthandDigits(digits);
  if (alias) {
    if (apiDigits === alias) score = Math.max(score, 980);
    else if (apiDigits.startsWith(alias)) score = Math.max(score, 940);
  }
  if (!queryHasExplicitApiState(digits)) {
    if (digits.length === 5 && apiDigits.length >= 10 && apiDigits.slice(5, 10) === digits) {
      score = Math.max(score, 720);
    } else if (digits.length >= 5 && apiDigits.endsWith(digits)) {
      score = Math.max(score, 700);
    } else if (apiDigits.includes(digits)) {
      score = Math.max(score, 560);
    }
  }
  return score;
}

/** Higher = better match. Used to surface "ANGEL EYES" above other ANGEL* wells. */
export function scoreFacilityNameMatch(name: string, api: string, query: string): number {
  // Strip quotes so UI/"angel eyes" and typed angel eyes score the same.
  const q = String(query || '')
    .toLowerCase()
    .trim()
    .replace(/^[\s"'“”‘’]+|[\s"'“”‘’]+$/g, '')
    .trim();
  if (!q) return 0;
  if (isNmOcdFacilityId(q)) {
    const identifier = String(api || '').toLowerCase().trim();
    return identifier === q ? 1000 : identifier.startsWith(q) ? 960 : 0;
  }
  if (looksLikeApiSearchQuery(q)) {
    return scoreApiNumberMatch(api, q);
  }
  const n = String(name || '').toLowerCase();
  const a = String(api || '').toLowerCase();
  const digits = q.replace(/\D/g, '');
  const apiDigits = a.replace(/\D/g, '');
  let score = 0;

  if (digits.length >= 5 && apiDigits) {
    if (apiDigits === digits) score = Math.max(score, 1000);
    else if (apiDigits.includes(digits)) score = Math.max(score, 700);
  }

  if (n === q) score = Math.max(score, 950);
  else if (n.startsWith(q)) score = Math.max(score, 880);
  else if (n.includes(q)) score = Math.max(score, 820);

  const compactQ = q.replace(/\s+/g, '');
  const compactN = n.replace(/\s+/g, '');
  if (compactN === compactQ) score = Math.max(score, 940);
  else if (compactN.includes(compactQ) && compactQ.length >= 4) score = Math.max(score, 800);

  const tokens = tokenizeFacilitySearchQuery(q);
  if (tokens.length > 1) {
    const hits = tokens.filter((t) => n.includes(t)).length;
    if (hits === tokens.length) {
      // All tokens present — boost above single-token partials from OR-style APIs
      score = Math.max(score, 780 + Math.min(40, tokens.length * 5));
      // Contiguous phrase already handled above (~820)
    } else if (hits > 0) {
      score = Math.max(score, hits * 40);
    }
  } else if (tokens.length === 1) {
    if (n.startsWith(tokens[0])) score = Math.max(score, 750);
    else if (n.includes(tokens[0])) score = Math.max(score, 600);
  }

  return score;
}

export function rankFacilitySearchHits<T>(
  hits: T[],
  query: string,
  getName: (hit: T) => string,
  getApi: (hit: T) => string = () => '',
): T[] {
  const q = String(query || '').trim();
  if (!q) return hits;
  const apiQuery = looksLikeApiSearchQuery(q);
  const scored = hits.map((hit) => ({
    hit,
    score: scoreFacilityNameMatch(getName(hit), getApi(hit), q),
    name: getName(hit),
  }));
  // API prefix queries (30-028) must not keep TX last-5 collisions scored 0.
  const kept = (apiQuery || isNmOcdFacilityId(q)) ? scored.filter((row) => row.score > 0) : scored;
  if (kept.length <= 1) return kept.map((row) => row.hit);
  return kept
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      if (apiQuery) {
        const apiCmp = compareApiNumberDigits(getApi(b.hit), getApi(a.hit));
        if (apiCmp) return apiCmp;
      }
      return a.name.localeCompare(b.name);
    })
    .map((row) => row.hit);
}
